import { Controller } from '@hotwired/stimulus';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import da from 'intl-tel-input/build/js/i18n/da/index.js';
import en from 'intl-tel-input/build/js/i18n/en/index.js';
import sv from 'intl-tel-input/build/js/i18n/sv/index.js';

const localeMap = {
  'da': da,
  'en': en,
  'sv': sv
}

export default class extends Controller {
  static targets = ['input', 'countryCode'];
  static values = {
    locale: String,
    validate: Boolean,
  }

  connect() {
    const storedCountry = this.countryCodeTarget.value;
    const locale = this.localeValue || 'da';

    this.phoneInput = intlTelInput(this.inputTarget, {
      nationalMode: true,
      i18n: localeMap[locale],
      initialCountry: storedCountry || 'auto',
      preferredCountries: ['dk', 'se', 'no', 'gb', 'fi'],
      loadUtils: () => import('intl-tel-input/build/js/utils.js'),
      formatOnDisplay: false,
      separateDialCode: true,
      geoIpLookup: (callback) => {
        if (storedCountry) {
          callback(storedCountry);
        } else {
          fetch('https://ipapi.co/json')
            .then(res => res.json())
            .then(data => {
              const countryCode = data.country_code?.toLowerCase();
              callback(countryCode);
            })
            .catch(() => callback('dk'));
        }
      },
      customPlaceholder: () => '',
      customContainer: 'iti-custom-container'
    });

    const style = document.createElement('style');
    style.textContent = `
      .iti {
        width: 100%;
        display: flex;
      }

      .iti__flag {
        background-image: url("/images/flags.png");
      }

      @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
        .iti__flag {
          background-image: url("/images/flags@2x.png");
        }
      }

      .iti__selected-flag {
        background: white !important;
        border: 1px solid #d6e6ea;
        border-right: none;
        border-radius: 4px 0 0 4px;
        padding: 0 6px 0 8px;
        min-width: 90px;
        display: flex;
        align-items: center;
      }

      .iti--separate-dial-code .iti__selected-flag {
        background: white !important;
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }

      .iti__selected-dial-code {
        background: #f4f6f8;
        font-size: 18px;
        font-weight: 500;
        height: 36px;
        line-height: 36px;
        padding: 0 4px;
        white-space: nowrap;
      }

      .iti--separate-dial-code input {
        padding-left: 12px !important;
        flex: 1;
        min-width: 0;
      }

      .iti input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #d6e6ea;
        border-radius: 0 4px 4px 0;
        font-family: "Roboto", sans-serif;
        font-size: 14px;
        line-height: 20px;
        color: #212529;
        height: 38px;
        background: white;
      }

      .iti input:focus {
        outline: none;
        border-color: #111332;
      }

      .iti__selected-flag:focus,
      .iti__selected-flag:hover {
        background: white !important;
      }

      .iti input::placeholder {
        color: #9CA3AF;
      }

      .iti__country-list {
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        border: 1px solid #d6e6ea;
        margin-top: 2px;
        background: white;
      }

      .iti__country {
        padding: 8px 10px;
        display: flex;
        align-items: center;
      }

      .iti__country-name {
        color: #374151;
        font-family: "Roboto", sans-serif
        font-size: 14px;
      }

      .iti__dial-code {
        color: #212529;
        font-family: "Roboto", sans-serif
        font-size: 14px;
      }

      .iti__country:hover {
        background-color: #f4f6f8;
      }

      .iti__country.iti__highlight {
        background-color: #F0F6F8;
      }
      
      .signer-form {
        .iti {
          .iti__country-container {
            .iti__selected-dial-code {
              height: 31px;
              border-radius: 4px;
              align-content: center;
              background: #FFF;
              color: #000;
              font-family: 'Roboto', sans-serif;
              font-size: 16px;
              font-style: normal;
              font-weight: 400;
              line-height: 16px;
              padding-top: 2px;
            }
          }
        }
      }
    `;
    document.head.appendChild(style);

    this.inputTarget.addEventListener('input', this.handleInputChange);
    this.inputTarget.addEventListener('countrychange', this.handleCountryChange);

    if (this.validateValue) {
      this.form = this.element.closest('form');
      if (this.form) this.form.addEventListener('submit', this.validateOnSubmit);
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const codeLength = this.inputTarget.parentNode.querySelector('.iti__selected-dial-code').offsetWidth
          if (codeLength > 39) {
            this.inputTarget.style.paddingLeft = '101px';
          } else {
            this.inputTarget.style.paddingLeft = '90px';
          }
          this.intersectionObserver.disconnect();
        }
      });
    });
    this.intersectionObserver.observe(this.inputTarget);
  }

  handleInputChange = (event) => {
    this.filterInput(event);
    this.handleCountryChange(event);
  }

  filterInput = (event) => {
    event.target.value = event.target.value.replace(/[^0-9\s()]/g, '');
  }

  handleCountryChange = (event) => {
    this.countryCodeTarget.value = this.phoneInput.getSelectedCountryData().iso2;
    this.clearValidationError();
  }

  validateOnSubmit = (event) => {
    if (this.inputTarget.value.trim() === '') return;
    if (this.phoneInput.isValidNumber() !== false) return;

    event.preventDefault();
    event.stopPropagation();
    this.showValidationError();
    this.inputTarget.focus();
  }

  showValidationError() {
    this.inputTarget.classList.add('is-invalid');
    if (!this.errorElement) {
      this.errorElement = document.createElement('div');
      this.errorElement.className = 'invalid-feedback d-block';
      this.errorElement.textContent = this.inputTarget.dataset.invalidMessage || 'Please enter a valid phone number';
      this.element.appendChild(this.errorElement);
    }
  }

  clearValidationError() {
    this.inputTarget.classList.remove('is-invalid');
    if (this.errorElement) {
      this.errorElement.remove();
      this.errorElement = null;
    }
  }

  disconnect() {
    this.inputTarget.removeEventListener('input', this.handleInputChange);
    this.inputTarget.removeEventListener('countrychange', this.handleCountryChange);
    if (this.form) this.form.removeEventListener('submit', this.validateOnSubmit);
    this.phoneInput.destroy();
  }
}
